import React, {FC, useEffect, useMemo, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {apiV2, auth, onAuthStateChanged} from "api/firebase";
import {fetchUser} from "store/auth/actions";
import {selectUser as selectUserFromStore} from "store/auth/selectors";
import PrivateRoute from "view/components/routing/PrivateRoute";
import Layout from "view/components/Layout";
import {
  createBrowserRouter,
  RouteObject,
  RouteProps,
  RouterProvider,
} from "react-router-dom";
import useUserGuidingIdentify from "hooks/useUserGuidingIdentify";
import {observabilityIdentify} from "tools/observability";
import {
  beginNoticeRedirect,
  clearSessionSwitchInProgress,
  isNoticeRedirectInFlight,
  subscribeToAuthSessionEvents,
} from "tools/auth/sessionSync";
import {resetUser} from "store/auth/resetUser";
import {getRegisteredStore} from "store/store-registry";
import useRedirectByAlias from "view/apps/useRedirectByAlias";
import {useMaintenanceModeStore} from "store/general/maintenanceMode.store";
import {SESSION_CHANGED_PATH} from "view/routes";
import commonRoutes from "./commonRoutes";
import MaintenanceMode from "./MaintenanceMode";

// Returns true when this tab considers itself authenticated locally. Uses
// two independent signals so a transient null on either side does not flip
// the answer:
//   - `auth.currentUser` — Firebase's view; can momentarily flicker to null
//     during cross-tab persistence sync.
//   - Redux `auth.id` — set by `fetchUser` after a successful `getMe`,
//     cleared by `resetUser`. Lags Firebase by one render but is stable.
// Either signal being truthy means "this tab was driving an authenticated
// route until just now". That is the condition under which we must
// redirect to /session-changed instead of letting any other handler nudge
// us toward /login.
const isAuthenticatedLocally = (): boolean => {
  if (auth.currentUser) {
    return true;
  }

  try {
    const state = getRegisteredStore().getState() as {
      auth?: {id?: string};
    };
    return Boolean(state.auth?.id);
  } catch {
    return false;
  }
};

const getRoutesByRole = (role?: string) => {
  switch (role) {
    case "organiser":
      return import("./Organiser").then((v) => v.default);
    case "supplier":
      return import("./Supplier").then((v) => v.default);
    case "admin":
      return import("./Admin").then((v) => v.default);
    case "exhibitor":
      return import("./Exhibitor").then((v) => v.default);
    default:
      return import("./unauthorisedRoutes").then((v) => v.default);
  }
};
const Apps: FC = () => {
  const [routesByRole, setRoutesByRole] = useState<RouteProps[]>([]);
  const [wasAuthStateChanged, setWasAuthStateChanged] = useState<boolean>(
    false
  );
  const {maintenanceMode, autoCatch} = useMaintenanceModeStore();

  useEffect(() => {
    const unsub = autoCatch();
    return () => {
      unsub();
    };
  }, [autoCatch]);

  const {role} = useSelector(selectUserFromStore);
  useUserGuidingIdentify();

  useRedirectByAlias();
  const dispatch = useDispatch();

  useEffect(() => {
    return subscribeToAuthSessionEvents((event) => {
      // Already on the notice page — nothing to do.
      if (window.location.pathname === SESSION_CHANGED_PATH) {
        return;
      }

      if (
        event.type !== "logout" &&
        event.type !== "login" &&
        event.type !== "switch-start"
      ) {
        return;
      }

      // Receiver tabs MUST NOT touch `clearSessionSwitchInProgress()` /
      // localStorage at all. The session-switch flag belongs to the
      // ORIGINATING tab — `markSessionSwitchInProgress()` writes it,
      // `signInWithCustomToken().then()` clears it. localStorage is shared
      // across tabs of the same origin, so a receiver-side clear destroys
      // the originator's own state and used to make `PrivateRoute` flip
      // `authorized` to false on the originator the instant Firebase
      // briefly fires `onAuthStateChanged(null)` between sign-out-of-old
      // and sign-in-of-new. That was the "logged in two tabs → got
      // immediately logged out" bug.
      if (!isAuthenticatedLocally()) {
        // Anonymous tab (e.g. parked on /login). Let `AnonymousRoute`'s
        // own `onAuthStateChanged` subscription drive the soft-navigation
        // once Firebase's cross-tab sync delivers the new user.
        return;
      }

      // Authenticated tab whose backing session was just replaced or
      // destroyed by another tab. Mark the redirect synchronously *before*
      // calling `window.location.replace` so the local Firebase listener,
      // `PrivateRoute`, `Apps.onAuthStateChanged`, and `onUnauthorized` can
      // all see the flag in the brief window between `replace()` returning
      // and the document actually unloading. Otherwise Firebase's own
      // cross-tab persistence sync (which fires its `onAuthStateChanged`
      // listeners on the FIREBASE_KEY storage event right after our event)
      // would race us to `<Navigate to=/login />` or call `onUnauthorized`
      // and re-introduce the cleanup cascade.
      beginNoticeRedirect();
      window.location.replace(SESSION_CHANGED_PATH);
    });
  }, []);

  useEffect(() => {
    let lastSeenUid: string | null = null;

    return onAuthStateChanged(async (user) => {
      // A cross-tab event already kicked off `window.location.replace
      // ('/session-changed')` in this tab. Firebase's own persistence sync
      // is firing this callback for the NEW user (or null) in the brief
      // window before the document actually unloads. Touching Redux
      // (`fetchUser` / `resetUser`) or hitting the API now would race the
      // half-replaced session and used to either shoot a 401-cascade
      // through `onUnauthorized` or briefly render the new user's data on
      // the dying page. The redirect handles teardown via natural reload.
      if (isNoticeRedirectInFlight()) {
        return;
      }

      if (user) {
        // Skip duplicate work: Firebase fires this callback on every token
        // refresh (~hourly) and on cross-tab sync. We only need to hydrate
        // Redux + reconnect the notification socket when the IDENTITY
        // actually changes, not on every refresh tick.
        if (lastSeenUid === user.uid) {
          return;
        }
        lastSeenUid = user.uid;

        clearSessionSwitchInProgress();
        observabilityIdentify(user);
        await apiV2.user.getMe();
        apiV2.notification.connect();

        dispatch(fetchUser());
        return;
      }

      lastSeenUid = null;

      // Firebase told us auth cleared (either from our own logout or from
      // another tab's logout, synced via Firebase IndexedDB/localStorage).
      // Reset Redux auth synchronously so stale `store.auth.id` doesn't keep
      // `Login.useEffect(id)` re-navigating to APP_PATH while Firebase is
      // null — that re-navigation plus `PrivateRoute`'s redirect to /login
      // forms an infinite loop.
      dispatch(resetUser());
      setWasAuthStateChanged(true);
    });
  }, [dispatch]);

  useEffect(() => {
    const loader = async () => {
      if (!role) return;

      setRoutesByRole(await getRoutesByRole(role));
    };
    if (role || wasAuthStateChanged) {
      loader();
    }
  }, [role, wasAuthStateChanged]);

  const router = useMemo(() => {
    const routes: RouteProps[] = [
      {
        path: "/",
        element: <PrivateRoute>{role ? <Layout /> : <div />}</PrivateRoute>,
        children: routesByRole,
      },
      ...commonRoutes,
      {
        path: "*",
        element: (
          <PrivateRoute>
            <div />
          </PrivateRoute>
        ),
      },
    ].map((route, index) => ({
      ...route,
      key: `${index}-${route.path}`,
      errorElement: <></>,
    }));

    return createBrowserRouter(routes as RouteObject[]);
  }, [role, routesByRole]);

  const isEnabledMaintenanceMode =
    !!role && role !== "admin" && maintenanceMode?.enabled;
  if (isEnabledMaintenanceMode) {
    return <MaintenanceMode />;
  }

  return <RouterProvider router={router} />;
};

export default Apps;
